import { useEffect, useState } from 'react';
import WhatsAppEventModal from './WhatsAppEventModal';
import { getTramite } from '@/services/tramiteService';
import { otrosTramitesService } from '@/otros-tramites/services/otrosTramitesService';
import { useAlert } from '@/hooks/useAlert';
import type { CobroPendiente } from '@/contabilidad/types';

type TramiteLike = {
  id: number | string;
  compradores?: Array<any>;
  vendedores?: Array<any>;
  otorgantes?: Array<any>;
  responsables?: Array<any>;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** El recordatorio es del saldo del trámite en esa moneda. */
  cobro: Pick<CobroPendiente, 'id' | 'origen' | 'tramiteId' | 'otroTramiteId' | 'moneda'>;
};

const WhatsAppCobroPendienteModal = ({ open, onOpenChange, cobro }: Props) => {
  const { errorAlert } = useAlert();
  const [tramiteLike, setTramiteLike] = useState<TramiteLike | null>(null);

  useEffect(() => {
    if (!open) {
      setTramiteLike(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        if (cobro.origen === 'otro_tramite' && cobro.otroTramiteId) {
          const record = await otrosTramitesService.getById(cobro.otroTramiteId);
          if (cancelled) return;
          setTramiteLike({
            id: cobro.otroTramiteId,
            otorgantes: record?.otorgantes ?? [],
            responsables: record?.responsables ?? [],
          });
        } else if (cobro.tramiteId) {
          const tramite = await getTramite({ id: cobro.tramiteId });
          if (cancelled) return;
          setTramiteLike({
            id: cobro.tramiteId,
            compradores: tramite?.compradores ?? [],
            vendedores: tramite?.vendedores ?? [],
          });
        } else {
          setTramiteLike({ id: cobro.tramiteId ?? cobro.otroTramiteId ?? '' });
        }
      } catch (error: any) {
        if (!cancelled) {
          errorAlert({ title: 'No se pudo cargar la información del trámite', exception: error });
          setTramiteLike({ id: cobro.tramiteId ?? cobro.otroTramiteId ?? '' });
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, cobro.id]);

  if (!open || !tramiteLike) {
    return null;
  }

  const contextOverrides =
    cobro.origen === 'otro_tramite'
      ? { otro_tramite_id: cobro.otroTramiteId, tramite_id: undefined, moneda: cobro.moneda }
      : { moneda: cobro.moneda };

  const recipientGroups =
    cobro.origen === 'otro_tramite'
      ? (['otorgantes', 'responsables'] as const)
      : (['compradores', 'vendedores'] as const);

  return (
    <WhatsAppEventModal
      open={open}
      onOpenChange={onOpenChange}
      tramite={tramiteLike}
      eventType="contabilidad.recordatorio_pago"
      title="Enviar recordatorio de pago por WhatsApp"
      description={`Se enviará un recordatorio del saldo pendiente (${cobro.moneda}).`}
      contextOverrides={contextOverrides}
      recipientGroups={[...recipientGroups]}
    />
  );
};

export default WhatsAppCobroPendienteModal;
